// src/components/common/UI.jsx
import React from 'react';
import './UI.css';

// ── Card ──────────────────────────────────────────────────
export function Card({ children, className = '', padding = true, ...props }) {
  return (
    <div className={`card ${padding ? 'card--padded' : ''} ${className}`} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, actions }) {
  return (
    <div className="card-header">
      <div>
        <h3 className="card-header__title">{title}</h3>
        {subtitle && <p className="card-header__sub">{subtitle}</p>}
      </div>
      {actions && <div className="card-header__actions">{actions}</div>}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────
const BADGE_MAP = {
  Healthy:    'success',
  Sick:       'danger',
  Pregnant:   'info',
  Quarantine: 'warning',
  Sold:       'muted',
  Deceased:   'muted',
  Cattle:     'earth',
  Sheep:      'success',
  Goat:       'warning',
  Pig:        'pink',
  Poultry:    'purple',
  Rabbit:     'info',
  Donkey:     'earth',
};

export function Badge({ label, variant }) {
  const v = variant || BADGE_MAP[label] || 'muted';
  return <span className={`badge badge--${v}`}>{label}</span>;
}

// ── Stat card ─────────────────────────────────────────────
export function StatCard({ label, value, sub, icon, color = 'green', trend }) {
  return (
    <div className={`stat-card stat-card--${color} animate-fadeUp`}>
      <div className={`stat-card__icon stat-card__icon--${color}`}>{icon}</div>
      <div className="stat-card__body">
        <div className="stat-card__label">{label}</div>
        <div className="stat-card__value">{value}</div>
        {sub  && <div className="stat-card__sub">{sub}</div>}
        {trend && (
          <div className={`stat-card__trend stat-card__trend--${trend.dir}`}>
            {trend.dir === 'up' ? '▲' : '▼'} {trend.text}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────
export function Spinner({ size = 24 }) {
  return (
    <div className="spinner" style={{ width: size, height: size }}>
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="var(--forest-200)" strokeWidth="2.5" />
        <path d="M12 2a10 10 0 0 1 10 10" stroke="var(--forest-600)" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="page-loader">
      <Spinner size={36} />
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="empty-state">
      <div className="empty-state__icon">{icon}</div>
      <h3 className="empty-state__title">{title}</h3>
      <p className="empty-state__desc">{description}</p>
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────
export function ProgressBar({ value, max = 100, color = 'green', showLabel = false }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  const col  = pct <= 20 ? 'red' : pct <= 50 ? 'amber' : color;
  return (
    <div className="progress">
      <div className={`progress__fill progress__fill--${col}`} style={{ width: `${pct}%` }} />
      {showLabel && <span className="progress__label">{pct}%</span>}
    </div>
  );
}

// ── Alert ─────────────────────────────────────────────────
export function Alert({ type = 'info', children }) {
  return <div className={`alert alert--${type}`}>{children}</div>;
}

// ── Table wrapper ─────────────────────────────────────────
export function Table({ headers, children, loading }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {loading
            ? <tr><td colSpan={headers.length} className="table__loading"><Spinner /></td></tr>
            : children
          }
        </tbody>
      </table>
    </div>
  );
}
